<template>
  <div class="on-task-create-labels-form">
    <label v-if="labelText">{{ labelText }}</label>
    <MultiSelect
      v-if="availableLabels.length > 0"
      :options="availableLabels"
      :selected-ids="selectedLabelIds"
      :label="''"
      :placeholder="s__('TaskForm|Select labels...')"
      :search-placeholder="s__('TaskForm|Search labels...')"
      :no-results-text="s__('TaskForm|No labels found')"
      @update:selected-ids="handleLabelIdsUpdate"
    />
    <div v-else class="muted" style="margin-bottom: 8px">{{ emptyMessage }}</div>
    <button
      v-if="canCreateLabel"
      class="btn-xs btn-secondary"
      style="margin-top: 8px"
      @click="showCreateModal = true"
    >
      {{ createButtonText }}
    </button>

    <!-- Create Label Modal -->
    <LabelModal
      v-model="showCreateModal"
      :title="createModalTitle"
      :creating="creating"
      :error="error"
      @submit="handleCreateLabel"
      @close="handleModalClose"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { s__ } from '@/locale'
import LabelModal from './LabelModal.vue'
import MultiSelect from './MultiSelect.vue'
import { useCreateLabel } from '@/composables/useCreateLabel'

const props = defineProps({
  scope: {
    type: String,
    required: true,
    validator: value => ['project', 'personal'].includes(value),
  },
  projectId: {
    type: String,
    default: null,
  },
  selectedLabelIds: {
    type: Array,
    required: true,
    default: () => [],
  },
  availableLabels: {
    type: Array,
    required: true,
    default: () => [],
  },
  canCreateLabel: {
    type: Boolean,
    default: false,
  },
  labelText: {
    type: String,
    required: true,
  },
  emptyMessage: {
    type: String,
    default: '',
  },
  createButtonText: {
    type: String,
    default: '',
  },
  createModalTitle: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:selectedLabelIds', 'refetch', 'update:availableLabels'])

const showCreateModal = ref(false)

const {
  creating,
  error,
  createLabel: createLabelMutation,
} = useCreateLabel(props.scope, props.projectId)

// Initialize selectedLabelIds - convert to strings for MultiSelect compatibility
// but preserve the original format when emitting
const selectedLabelIds = ref([...props.selectedLabelIds].map(id => String(id)))
const isUpdatingFromProps = ref(false)

// Helper to compare arrays (order-independent)
function arraysEqual(a, b) {
  if (!a || !b) return a === b
  if (a.length !== b.length) return false
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  return sortedA.every((val, idx) => val === sortedB[idx])
}

watch(
  () => props.selectedLabelIds,
  async newIds => {
    const newIdsArray = [...(newIds || [])].map(id => String(id))
    const currentIdsArray = [...(selectedLabelIds.value || [])]
    // Only update if values actually changed to avoid unnecessary updates
    if (!arraysEqual(newIdsArray, currentIdsArray)) {
      isUpdatingFromProps.value = true
      // Force a new array reference to ensure Vue detects the change
      selectedLabelIds.value = newIdsArray.slice()
      // Reset flag after next tick to allow user-initiated updates
      await nextTick()
      isUpdatingFromProps.value = false
    }
  },
  { immediate: true }
)

watch(
  selectedLabelIds,
  newIds => {
    // Don't emit if the change came from props update (to prevent circular updates)
    if (!isUpdatingFromProps.value) {
      // GraphQL ID type is a string, so keep IDs as strings
      // The backend service will convert them to integers when needed
      emit('update:selectedLabelIds', [...newIds])
    }
  },
  { deep: true }
)

async function handleLabelIdsUpdate(newIds) {
  // Update selectedLabelIds - this will trigger the watcher which emits to parent
  // Ensure IDs are strings for consistency
  const stringIds = newIds.map(id => String(id))
  // Force a new array reference to ensure Vue detects the change
  selectedLabelIds.value = stringIds.slice()
  // Wait for next tick to ensure the watcher has fired and parent has updated
  await nextTick()
  // The parent should update props.selectedLabelIds, which will update our selectedLabelIds.value
  // But we also need to ensure MultiSelect receives the updated value
}

function handleModalClose() {
  showCreateModal.value = false
}

async function handleCreateLabel(formData) {
  const result = await createLabelMutation(formData.title, formData.color)

  if (!result.success) {
    return // Error is already set in the composable
  }

  // Add the new label to the available labels list
  if (result.label) {
    const updatedLabels = [...props.availableLabels, result.label]
    emit('update:availableLabels', updatedLabels)

    // Auto-select the newly created label
    const currentIds = Array.isArray(selectedLabelIds.value) ? [...selectedLabelIds.value] : []
    const newLabelId = String(result.label.id)
    if (!currentIds.includes(newLabelId)) {
      selectedLabelIds.value = [...currentIds, newLabelId]
    }
  }

  // Close modal
  showCreateModal.value = false

  // Emit refetch for parent component to update its data
  emit('refetch')
}
</script>

<style scoped>
.on-task-create-labels-form {
  flex: 1;
}

.label-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  margin-top: 4px;
}

.label-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.label-checkbox input[type='checkbox'] {
  cursor: pointer;
}

.label-badge {
  display: inline-block;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
}

.btn-xs {
  padding: 4px 12px;
  font-size: 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.muted {
  color: #999;
  font-size: 14px;
}
</style>
